import React, { useEffect, useState } from 'react';
import { DrawerComponent } from 'components/Drawer';
import { api } from 'services/api';

type KoinTier = {
  id: number;
  name: string;
  koin_quantity: number;
  discount_percentage: number;
  total_price_brl: number;
  unit_price_brl: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  idToken: string | null;
};

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

export const ComprarKoinsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  accessToken,
  idToken,
}) => {
  const [tiers, setTiers] = useState<KoinTier[]>([]);
  const [basePriceBrl, setBasePriceBrl] = useState<number>(0);
  const [selectedTier, setSelectedTier] = useState<KoinTier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchTiers = async () => {
      try {
        const { data } = await api.get('lya/koin-tiers', {
          headers: { Authorization: `Bearer ${accessToken}`, idToken },
        });
        setTiers(data.data.tiers);
        setBasePriceBrl(data.data.base_price_brl);
        if (data.data.tiers.length > 0) setSelectedTier(data.data.tiers[0]);
      } catch (e) {
        setError('Não foi possível carregar os pacotes de koins.');
      }
    };
    fetchTiers();
  }, [isOpen, accessToken, idToken]);

  const handleBuy = async () => {
    if (!selectedTier) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(
        'lya/buy',
        {
          id_tier: selectedTier.id,
          koin_quantity: selectedTier.koin_quantity,
          amount_brl: selectedTier.total_price_brl,
          success_url: `${window.location.origin}/lya/koin/success`,
          cancel_url: window.location.href,
        },
        { headers: { Authorization: `Bearer ${accessToken}`, idToken } },
      );
      if (data.data?.checkout_url) {
        window.open(data.data.checkout_url, '_blank');
        onClose();
      }
    } catch (e) {
      setError('Erro ao iniciar compra. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DrawerComponent
      open={isOpen}
      size='default'
      titleClose='Comprar koins'
      onCompleteClose={onClose}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          paddingBottom: '24px',
        }}
      >
        {/* Description */}
        <p style={{ color: '#555', fontSize: '14px', margin: '16px 0 20px' }}>
          Os pacotes de koins são utilizados quando o saldo do plano acaba e são
          válidos por um ano a partir da data da compra.
        </p>

        {error && (
          <p
            style={{ color: '#c0392b', fontSize: '13px', marginBottom: '12px' }}
          >
            {error}
          </p>
        )}

        {/* Tier list */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            flex: 1,
          }}
        >
          {tiers.map(tier => {
            const isSelected = selectedTier?.id === tier.id;
            const originalPrice = basePriceBrl * tier.koin_quantity;

            return (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier)}
                style={{
                  border: `2px solid ${isSelected ? '#E0234E' : '#e0e0e0'}`,
                  borderRadius: '10px',
                  padding: '14px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: '#fff',
                }}
              >
                {/* Radio indicator */}
                <div
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: isSelected ? `4px solid #E0234E` : `2px solid #ccc`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                ></div>

                {/* Tier info */}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#808080', fontSize: '12px' }}>
                    {tier.koin_quantity.toLocaleString('pt-BR')} koins
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>
                    {tier.name}
                  </div>
                </div>

                {/* Price + discount */}
                <div
                  style={{
                    textAlign: 'right',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '40px',
                  }}
                >
                  {tier.discount_percentage > 0 && (
                    <span
                      style={{
                        background: '#D93E26',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 700,
                        borderRadius: '4px',
                        padding: '1px 5px',
                        height: 'fit-content',
                      }}
                    >
                      -{tier.discount_percentage}%
                    </span>
                  )}
                  <div>
                    {tier.discount_percentage > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          justifyContent: 'flex-end',
                          marginBottom: '2px',
                        }}
                      >
                        <span
                          style={{
                            color: '#999',
                            fontSize: '12px',
                            textDecoration: 'line-through',
                          }}
                        >
                          {formatBRL(originalPrice)}
                        </span>
                      </div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: '15px' }}>
                      {formatBRL(tier.total_price_brl)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Buy button — sticky at bottom */}
        <button
          onClick={handleBuy}
          disabled={loading || !selectedTier}
          style={{
            marginTop: '24px',
            width: '100%',
            padding: '14px',
            background: loading || !selectedTier ? '#ccc' : '#E0234E',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || !selectedTier ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: '15px',
          }}
        >
          {loading ? 'Aguarde...' : 'Comprar pacote'}
        </button>
      </div>
    </DrawerComponent>
  );
};
